import React from 'react';
import Dispatcher from '../dispatchers/Dispatcher';
import Offset from '../lib/Offset';
import { ReactComponent as RinkLtr } from '../assets/rink-ltr.svg';
import { ReactComponent as RinkRtl } from '../assets/rink-rtl.svg';

export default class InterfaceRink extends React.Component {

    constructor(props) {
        super(props)

        this.state = {
            /**
             * The current position/coordinates of the puck
             */
            puck: null
        };
    }

    changeDirection() {
        Dispatcher.dispatch({ type: 'change-direction' });
    }

    handleButtonPress() {
        window.buttonPressTimer = window.setTimeout(() => {
            Dispatcher.dispatch({ type: 'change-direction' });
        }, 750);
    }

    handleButtonRelease () {
        window.clearTimeout(window.buttonPressTimer);
    }

    handleClick(area, e, name) {
        let target = area;

        let isTeam;
        let lockedSpot = true;
        let dir = null;

        if (target === 'free') {
            lockedSpot = false;

            // The rink is an inline <svg> element (not an <img>), so it doesn't expose plain numeric
            // .width / .height / offsetParent properties. getBoundingClientRect() gives us the
            // viewport-relative rect, and event.clientX/Y are already viewport-relative, so no
            // scroll-offset bookkeeping is needed here.
            const rinkEl = document.getElementById('rink');
            const rect = rinkEl.getBoundingClientRect();
            const percXNum = (e.clientX - rect.left) / rect.width;
            const percYNum = (e.clientY - rect.top) / rect.height;
            target = `${percXNum.toFixed(4)},${percYNum.toFixed(4)}`;

            const percX = target.split(',')[0];

            if (this.props.direction === 'right') {
                isTeam = percX > 0.5;
            } else {
                isTeam = percX < 0.5;
            }

            dir = percX < 0.5 ? 'left' : 'right';

            // when we have a faceoff, the user can't click freely.
            if (this.props.faceoff || !this.props.iceLock) {
                return;
            }
        }

        if (name) { // this the same as above checking for 'free' -- faceoffs
            const side = this.props.direction;

            if (name === 'center') {
                dir = 'center';
            } else {
                const split = name.split('_');

                dir = split[split.length - 1] === side ? 'attacking' : 'defending';
            }
        }

        const danger = e.target.dataset.danger || null;

        // Since giveaways/takeaways can now use the ice, we need to override all previous logic and
        // explicitly set the team to our team every time we have one of those eventss
        if (this.props.lockedEvent && ['turnover', 'takeaway'].includes(this.props.lockedEvent.type)) {
            isTeam = true;
        }

        const sendClick = () => {
            Dispatcher.dispatch({
                type: 'ice-clicked',
                data: {
                    target: target,
                    playerLock: this.determinePlayerLock(isTeam),
                    isTeam: isTeam,
                    lockedSpot: lockedSpot,
                    time: this.props.time,
                    side: dir,
                    danger: danger,
                    zoneName: name
                }
            });
        }

        // if the user clicked a faceoff spot, just send the click and return, otherwise
        // load up the puck position in state
        if (typeof target === 'number') {
            sendClick();
            return;
        } else {
            this.setState({ puck: target });
        }

        /**
         * When the opponent clicked, we wait for 1 second so the puck sticks to the screen no matter what.
         * Then, we enter the loop of checking for the locked event and waiting to remove the check/puck
         * until we have no locked even in props.
         *
         * Otherwise, we send the click and initialize the checker to check for props to remove the puck
         * as soon as the user has completed the entire locked event (modal, etc.).
         */
        if (!isTeam) {
            /**
             * Whenever the ice is clicked for an opponent goal, we have to wait before registering the click
             * and sending the dispatcher action. If we don't do this the puck will not "stick" before the modal
             * opens... it will just open instantly. Erik wants the puck to stick on the ice before the modal opens.
             */
            if (this.props.lockedEvent && this.props.lockedEvent.type === 'goal') {
                window.setTimeout(sendClick, 500);
            } else {
                sendClick();
            }
        } else {
            sendClick();
        }

        this.initializeChecker();
    }

    determinePlayerLock(isTeam) {
        const lockedEvent = this.props.lockedEvent;
        if (lockedEvent) {
            const button = this.findButton(lockedEvent);
            if (button) {
                return !!button.track_player;
            }
            return this.props.blockLock || isTeam;
        } else {
            return this.props.blockLock || isTeam;
        }
    }

    /**
     * The configurable buttons next to the attack-direction arrow. Positions 1-3 sit on
     * the left of the arrow, 4-6 on the right. Driven entirely by the team's configuration.
     */
    buttons() {
        return this.props.configuration.buttons || [];
    }

    leftButtons() {
        return this.buttons().filter(b => b.position <= 3);
    }

    rightButtons() {
        return this.buttons().filter(b => b.position >= 4);
    }

    // Find the configured button that produced a given locked event.
    findButton(lockedEvent) {
        return this.buttons().find(b => {
            if (b.custom) {
                return lockedEvent.type === 'custom' && lockedEvent.custom_event_id === b.id;
            }
            return b.event_key === lockedEvent.type;
        });
    }

    /**
     * Loop every 250ms until there is no locked event prop. When it sees that no locked
     * event is present, it will clear the interval and remove the puck position from state.
     */
    initializeChecker = () => {
        window.puckInterval = window.setInterval(() => {
            if (!this.props.lockedEvent || this.props.lockedEvent.type === 'faceoff') {
                window.clearInterval(window.puckInterval);
                this.setState({ puck: null });
            }

        }, 500);
    }

    renderFaceoffGuides() {
        if (this.props.faceoff) {
            return [
                {section: 'top_left', position: 1}, {section: 'middle_top_left', position: 3}, {section: 'middle_top_right', position: 4},
                {section: 'top_right', position: 2}, {section: 'bottom_left', position: 2}, {section: 'middle_bottom_left', position: 4},
                {section: 'middle_bottom_right', position: 3}, {section: 'bottom_right', position: 1}, {section: 'center', position: 5}
            ].map((section, idx) => {
                return (
                    <div
                        key={idx}
                        onClick={(e) => this.handleClick(section.position, e, section.section)}
                        className={`faceoff-guide ${section.section} ${this.props.lockedEvent && this.props.lockedEvent.zoneName === section.section ? 'active-guide' : ''}`}>
                    </div>
                );
            });
        } else {
            return '';
        }
    }

    renderDangerZones() {
        return [
            {zone: 'high_danger', danger: 'high'}, {zone: 'mid_danger_top', danger: 'medium'},
            {zone: 'mid_danger_bottom', danger: 'medium'}, {zone: 'mid_danger', danger: 'medium'},

            {zone: 'high_danger left', danger: 'high'}, {zone: 'mid_danger_top left', danger: 'medium'},
            {zone: 'mid_danger_bottom left', danger: 'medium'}, {zone: 'mid_danger left', danger: 'medium'},
        ].map((zone,idx) => {
            return (
                <div
                    data-danger={zone.danger}
                    key={idx}
                    onClick={(e) => this.handleClick('free', e, null)}
                    className={`danger-zone ${zone.zone}`}>
                </div>
            );
        });
    }

    renderShot() {
        // if (!this.props.lockedEvent || !this.props.lockedEvent.ice || typeof this.props.lockedEvent.ice === 'number') {
        //     return '';
        // }

        /**
         * We now only check state for puck, since it's all controlled there instead of the store
         */
        if (!this.state.puck || (this.props.lockedEvent && this.props.lockedEvent.ice && typeof this.props.lockedEvent.ice === 'number')) {
            return '';
        }

        // const shot = Offset.formatPercentage(this.props.lockedEvent.ice);
        const shot = Offset.formatPercentage(this.state.puck);

        return (
            <div className="shot" style={{ left: shot.left, top: shot.top }}></div>
        );
    }

    skipFaceoff() {
        Dispatcher.dispatch({
            type: 'skip-faceoff'
        });
    }

    /**
     * Next to the rink direction button we display an action button based on if the faceoff is locked or not.
     * In that case we show the Skip Faceoff button, otherwise we show the Video Stamp button.
     */
    renderRinkActionButton() {
        if (this.props.faceoff) {
            return this.renderSkipFaceoffButton()
        } else {
            return null
            // return this.renderVideoStampButton()
        }
    }

    renderSkipFaceoffButton() {
        return (
            <div className={`btn flex-btn`} onClick={this.skipFaceoff}>
                <span>Skip Faceoff</span>
            </div>
        )
    }

    // renderVideoStampButton() {
    //     return (
    //         <div className='btn direction-indicator skip-faceoff-margin' onClick={this.videoStamp}>
    //             <div className='test'>Video Stamp</div>
    //         </div>
    //     )
    // }

    // videoStamp = () => {
    //     Dispatcher.dispatch({
    //         type: 'video-stamp',
    //         data: {
    //             time: this.props.time
    //         }
    //     })
    // }

    handleKickedOut() {
        Dispatcher.dispatch({
            type: 'faceoff-button-click',
            data: {
                item: 'result',
                value: 'kicked_out'
            }
        });
    }

    /**
     * Dispatch a click for any configured button. Hardcoded events keep their original
     * section/value semantics so the existing GameStore paths and downstream
     * reports/CSV continue to work; custom events carry their name + button id.
     */
    handleButtonClick(button) {
        const common = {
            time: this.props.time,
            track_players: button.track_player,
            track_location: button.track_location,
            track_outcome: button.track_outcome,
            track_teams: button.track_team
        };

        if (button.custom) {
            Dispatcher.dispatch({
                type: 'interface-button-click',
                data: {
                    ...common,
                    section: 'custom',
                    custom: true,
                    value: button.custom_name,
                    custom_name: button.custom_name,
                    custom_event_id: button.id
                }
            });
            return;
        }

        if (button.event_key === 'battle') {
            Dispatcher.dispatch({
                type: 'interface-button-click',
                data: { ...common, section: 'battle', value: 'battle' }
            });
            return;
        }

        if (button.event_key === 'pass_failure') {
            Dispatcher.dispatch({
                type: 'interface-button-click',
                data: { ...common, section: 'pass_failure', value: 'pass_failure' }
            });
            return;
        }

        // turnover / takeaway / check / backcheck -> the original "top_right" flow.
        Dispatcher.dispatch({
            type: 'interface-button-click',
            data: { ...common, section: 'top_right', value: button.event_key }
        });
    }

    // Active/inactive highlight for a button based on the current locked event.
    buttonActive(button) {
        if (!this.props.lockedEvent) {
            return '';
        }
        return this.findButton(this.props.lockedEvent) === button ? 'active' : 'inactive';
    }

    // Render a button's label, supporting multi-line custom events (rows joined by "\n").
    renderButtonLabel(button) {
        const lines = (button.label || '').split('\n');
        return lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ));
    }

    renderButton(button) {
        return (
            <div
                key={button.position}
                style={{ padding: '5px' }}
                className={`btn flex-btn ${this.buttonActive(button)}`}
                onClick={() => this.handleButtonClick(button)}>
                <span style={{ lineHeight: '1.1em' }}>{this.renderButtonLabel(button)}</span>
            </div>
        );
    }

    render() {
        return (
            <div
                className="section"
                id="ice">

                <div style={{ display: 'flex', gap: '0.5em' }}>

                    {this.renderRinkActionButton()}

                    {
                        !this.props.faceoff && (
                            <span style={{ flex: 1, display: 'flex' }}>
                                { this.leftButtons().map(button => this.renderButton(button)) }
                            </span>
                        )
                    }

                    <div
                        onTouchStart={() => this.handleButtonPress()}
                        onTouchEnd={() => this.handleButtonRelease()}
                        onMouseDown={() => this.handleButtonPress()}
                        onMouseUp={() => this.handleButtonRelease()}
                        onMouseLeave={() => this.handleButtonRelease()}
                        className={`btn flex-btn direction-indicator ${this.props.time.minuteBump ? 'highlighted' : ''}`}>
                        <span>
                            <img
                                className={`direction ${this.props.direction === 'left' ? 'left' : 'right'}`}
                                src="ui-direction-arrow.svg"
                                style={{ width: '115px' }}
                                alt="rink" />
                        </span>
                    </div>

                    {
                        this.props.faceoff && (
                            <div className={`btn flex-btn`} onClick={this.handleKickedOut}>
                                <span>Kicked Out</span>
                            </div>

                        )
                    }

                    {
                        !this.props.faceoff && (
                            <span style={{ flex: '1', display: 'flex' }}>
                                { this.rightButtons().map(button => this.renderButton(button)) }
                            </span>
                        )
                    }

                </div>

                <div className={`rink ${(this.props.playerLock || this.props.faceoffLock) && !this.props.iceLock ? 'inactive' : ''}`} style={{ position: 'relative' }}>
                    {this.renderFaceoffGuides()}
                    {this.renderDangerZones()}
                    {
                        // Rendered as an inline <svg> (via SVGR's ReactComponent import) rather than
                        // an <img src="...">. This eliminates the separate HTTP fetch for the rink
                        // asset, which was the root cause of the "disappearing rink" bug on flaky
                        // tournament WiFi. Both directions are bundled into the JS, so flipping
                        // direction is instant and never hits the network.
                        this.props.direction === 'right' ? (
                            <RinkLtr
                                id="rink"
                                data-danger="low"
                                onClick={(e) => this.handleClick('free', e, null)}
                                role="img"
                                aria-label="Perfectly Measured Ice Rink"
                                style={{ width: '100%', height: 'auto', display: 'block' }} />
                        ) : (
                            <RinkRtl
                                id="rink"
                                data-danger="low"
                                onClick={(e) => this.handleClick('free', e, null)}
                                role="img"
                                aria-label="Perfectly Measured Ice Rink"
                                style={{ width: '100%', height: 'auto', display: 'block' }} />
                        )
                    }
                    {this.renderShot()}
                </div>
            </div>
        );
    }
}
